import { useDeviceStore } from "@/stores/deviceStore";
import { storeToRefs } from "pinia";
import { ref, computed, Ref } from "vue";

/**
 * useVerticalSwipe
 * @param threshold 슬라이드 업/다운으로 인식할 최소 Y 이동량 (px, 기본 50)
 * @param maxHeightPx 최대 높이 (px, 기본값은 화면 높이의 80%)
 * @param shouldCheckKeyboard 키보드 체크 여부
 * @param baseHeightPx 기본 높이 (px, 기본값 165)
 */
export const useVerticalSwipe = (
  threshold = 50,
  maxHeightPx = window.innerHeight * 0.8,
  shouldCheckKeyboard = false,
  baseHeightPx = 165
) => {
  // 터치 시작 위치
  const startY = ref(0);
  // 터치 중 이동량 (음수일수록 위로 드래그)
  const deltaY = ref(0);
  // 드래그 중인 상태 플래그
  const isDragging = ref(false);
  // 슬라이드 업이 감지된 상태 플래그
  const swipedUp = ref(false);
  // 실제 콘텐츠 높이
  const actualContentHeight = ref(0);

  const BASE_HEIGHT = baseHeightPx;

  const { isShowingKeyboard } = storeToRefs(useDeviceStore());

  // 확장 시 사용할 높이 계산
  const getExpandedHeight = () => {
    const baseHeight = BASE_HEIGHT; // 기본 높이 (px)
    const headerHeight = 24; // ExpandableModal 헤더 높이
    const minHeight = baseHeight; // 최소 확장 높이

    if (actualContentHeight.value > 0) {
      // 실제 콘텐츠 높이 + 헤더 높이
      const neededHeight = actualContentHeight.value + headerHeight;
      // 최소 높이와 최대 높이 사이에서 적절한 높이 결정
      return Math.min(Math.max(neededHeight, minHeight), maxHeightPx);
    }

    return maxHeightPx; // 콘텐츠 높이를 모르는 경우 최대 높이 사용
  };

  // 드래그 중 컨테이너 높이 변화를 위한 스타일
  const containerStyle = computed(() => {
    if (shouldCheckKeyboard && isShowingKeyboard.value) {
      return {
        display: "none",
      };
    }

    const baseHeight = BASE_HEIGHT; // 기본 높이 (px)
    const expandedHeight = getExpandedHeight();

    if (isDragging.value) {
      if (swipedUp.value) {
        // 확장된 상태에서 드래그 중일 때
        if (deltaY.value > 0) {
          // 아래로 드래그: 높이 감소
          const heightDecrease = Math.min(
            deltaY.value,
            expandedHeight - baseHeight
          );
          const newHeight = Math.max(
            expandedHeight - heightDecrease,
            baseHeight
          );
          return {
            height: `${newHeight}px`,
            transition: "none",
            touchAction: "pan-y",
          };
        } else {
          // 위로 드래그: 확장 높이 유지
          return {
            height: `${expandedHeight}px`,
            transition: "none",
            touchAction: "pan-y",
          };
        }
      } else {
        // 축소된 상태에서 드래그 중일 때
        if (deltaY.value < 0) {
          // 위로 드래그: 높이 증가
          const heightIncrease = Math.abs(deltaY.value);
          const newHeight = Math.min(
            baseHeight + heightIncrease,
            expandedHeight
          );
          return {
            height: `${newHeight}px`,
            transition: "none",
            touchAction: "pan-y",
          };
        } else {
          // 아래로 드래그: 기본 높이 유지
          return {
            height: `${baseHeight}px`,
            transition: "none",
            touchAction: "pan-y",
          };
        }
      }
    }

    return {
      height: swipedUp.value ? `${expandedHeight}px` : `${baseHeight}px`,
      transition: "height 0.2s ease",
      touchAction: "pan-y",
    };
  });

  /** 터치 시작 */
  const onTouchStart = (e: TouchEvent): void => {
    startY.value = e.touches[0].clientY;
    deltaY.value = 0;
    isDragging.value = true;
  };

  /** 터치 이동 */
  const onTouchMove = (e: TouchEvent): void => {
    const currentY = e.touches[0].clientY;
    deltaY.value = currentY - startY.value;
  };

  /** 터치 종료 */
  const onTouchEnd = (): void => {
    isDragging.value = false;

    if (swipedUp.value) {
      // 확장된 상태에서 아래로 스와이프하면 축소
      if (deltaY.value >= threshold) {
        swipedUp.value = false;
      }
    } else {
      // 축소된 상태에서 위로 스와이프하면 확장
      if (deltaY.value <= -threshold) {
        swipedUp.value = true;
      }
    }

    // 드래그가 끝나면 deltaY가 transition 이후 0으로 돌아가도록
    deltaY.value = 0;
  };

  // 외부에서 제어할 수 있는 확장 상태 설정 함수
  const setExpanded = (expanded: boolean) => {
    swipedUp.value = expanded;
  };

  // 실제 콘텐츠 높이 설정 함수
  const setContentHeight = (height: number) => {
    actualContentHeight.value = height;
  };

  return {
    containerStyle,
    onTouchStart,
    onTouchMove,
    onTouchEnd,
    swipedUp,
    setExpanded,
    setContentHeight,
  };
};
